import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { useAuth, Organization, AppUser, UserRole, ConsultingFirm, app } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import LanguageSelector from '../components/LanguageSelector';
import { CloneOrgModal, UserTable, AddUserForm, OrgManageUsers } from '../components/shared';
import './AdminPage.css';

// Helper to format Firestore Timestamp to readable date
const formatDate = (timestamp: Timestamp | { _seconds: number; _nanoseconds: number } | undefined): string => {
  if (!timestamp) return '-';

  let date: Date;
  if (typeof (timestamp as Timestamp).toDate === 'function') {
    date = (timestamp as Timestamp).toDate();
  } else if ('_seconds' in timestamp) {
    date = new Date(timestamp._seconds * 1000);
  } else {
    return '-';
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface FirmWithStats extends ConsultingFirm {
  consultantCount?: number;
  clientCount?: number;
}

interface PendingUser extends AppUser {
  isPending?: boolean;
}

export const AdminPage: React.FC = () => {
  const {
    user,
    isAdmin,
    isOrgAdmin,
    organization,
    createOrganization,
    getOrganizations,
    deleteOrganization,
    cloneOrganization,
    createUser,
    getUsers,
    getPendingUsers,
    updateUserRole,
    deleteUser,
    deletePendingUser,
    selectOrganization,
    logout,
  } = useAuth();
  const { t, language } = useLocale();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<PendingUser[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // New organization form
  const [showNewOrgForm, setShowNewOrgForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgLanguage, setNewOrgLanguage] = useState<'he' | 'en' | 'ar'>(language);
  const [newOrgEmoji, setNewOrgEmoji] = useState('');
  const [newOrgVfp, setNewOrgVfp] = useState('');

  // New user form
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUserOrgId, setNewUserOrgId] = useState('');

  // Clone organization modal
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSourceOrg, setCloneSourceOrg] = useState<Organization | null>(null);

  // Org users modal
  const [showOrgUsersModal, setShowOrgUsersModal] = useState(false);
  const [orgUsersOrg, setOrgUsersOrg] = useState<Organization | null>(null);

  // Consulting firms state
  const [consultingFirms, setConsultingFirms] = useState<FirmWithStats[]>([]);
  const [firmsLoading, setFirmsLoading] = useState(false);
  const [showNewFirmForm, setShowNewFirmForm] = useState(false);
  const [newFirmName, setNewFirmName] = useState('');
  const [newFirmLeadEmail, setNewFirmLeadEmail] = useState('');
  const [isCreatingFirm, setIsCreatingFirm] = useState(false);

  const functions = getFunctions(app, 'us-central1');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        if (isAdmin) {
          const orgs = await getOrganizations();
          setOrganizations(orgs);
        }

        const usersList = await getUsers(selectedOrgId || undefined);
        const pendingList = await getPendingUsers(selectedOrgId || undefined);
        const pendingWithFlag = pendingList.map(u => ({ ...u, isPending: true }));
        setUsers([...usersList, ...pendingWithFlag]);
      } catch (error) {
        console.error('Error loading data:', error);
      }
      setIsLoading(false);
    };

    loadData();
  }, [isAdmin, selectedOrgId, getOrganizations, getUsers, getPendingUsers]);

  // Load consulting firms (admin only)
  useEffect(() => {
    const loadFirms = async () => {
      if (!isAdmin) return;
      setFirmsLoading(true);
      try {
        const listConsultingFirms = httpsCallable(functions, 'listConsultingFirms');
        const result = await listConsultingFirms({});
        setConsultingFirms((result.data as any).firms || []);
      } catch (error) {
        console.error('Error loading consulting firms:', error);
      }
      setFirmsLoading(false);
    };

    loadFirms();
  }, [isAdmin, functions]);

  // Handle create consulting firm
  const handleCreateFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFirmName.trim() || !newFirmLeadEmail.trim()) return;

    setIsCreatingFirm(true);
    try {
      const createConsultingFirm = httpsCallable(functions, 'createConsultingFirm');
      const result = await createConsultingFirm({
        name: newFirmName.trim(),
        leadEmail: newFirmLeadEmail.trim(),
        branding: { firmName: newFirmName.trim() }
      });
      const newFirm = (result.data as any).firm;
      setConsultingFirms([...consultingFirms, newFirm]);
      setNewFirmName('');
      setNewFirmLeadEmail('');
      setShowNewFirmForm(false);
    } catch (error: any) {
      console.error('Error creating firm:', error);
      alert(error.message || 'Failed to create consulting firm');
    }
    setIsCreatingFirm(false);
  };

  // Handle delete consulting firm
  const handleDeleteFirm = async (firmId: string) => {
    if (!window.confirm(t('admin.deleteFirmConfirm'))) {
      return;
    }

    try {
      const deleteConsultingFirm = httpsCallable(functions, 'deleteConsultingFirm');
      await deleteConsultingFirm({ firmId });
      setConsultingFirms(consultingFirms.filter(f => f.id !== firmId));
    } catch (error: any) {
      console.error('Error deleting firm:', error);
      alert(error.message || 'Failed to delete consulting firm');
    }
  };

  // Handle create organization
  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    try {
      const org = await createOrganization(newOrgName.trim(), {
        language: newOrgLanguage,
        emoji: newOrgEmoji || undefined,
        companyVfp: newOrgVfp || undefined,
      });
      setOrganizations([...organizations, org]);
      setNewOrgName('');
      setNewOrgLanguage(language);
      setNewOrgEmoji('');
      setNewOrgVfp('');
      setShowNewOrgForm(false);
    } catch (error) {
      console.error('Error creating organization:', error);
    }
  };

  // Handle delete organization
  const handleDeleteOrg = async (orgId: string) => {
    if (!window.confirm(t('admin.deleteOrgConfirm'))) {
      return;
    }

    try {
      await deleteOrganization(orgId);
      setOrganizations(organizations.filter(o => o.id !== orgId));
      if (selectedOrgId === orgId) {
        setSelectedOrgId(null);
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
    }
  };

  // Handle open clone modal
  const handleOpenCloneModal = (org: Organization) => {
    setCloneSourceOrg(org);
    setShowCloneModal(true);
  };

  // Handle open org users modal
  const handleOpenOrgUsers = (org: Organization) => {
    setOrgUsersOrg(org);
    setShowOrgUsersModal(true);
  };

  // Handle clone organization
  const handleCloneOrg = async (newName: string, includeEmployees: boolean) => {
    if (!cloneSourceOrg) return;

    try {
      console.log('Cloning organization:', cloneSourceOrg.id, 'to', newName, 'includeEmployees:', includeEmployees);
      const newOrg = await cloneOrganization(cloneSourceOrg.id, newName, includeEmployees);
      console.log('Clone successful:', newOrg);
      setOrganizations([...organizations, newOrg]);
      setShowCloneModal(false);
      setCloneSourceOrg(null);
    } catch (error: any) {
      console.error('Error cloning organization:', error);
      alert(`Failed to clone organization: ${error.message || 'Unknown error'}`);
      throw error; // Re-throw so the modal knows it failed
    }
  };

  // Handle select organization
  const handleSelectOrg = async (orgId: string) => {
    setSelectedOrgId(orgId);
    await selectOrganization(orgId);
  };

  // Handle reassign organization to different firm
  const handleReassignOrgToFirm = async (orgId: string, firmId: string | null) => {
    try {
      const reassignOrgToFirm = httpsCallable(functions, 'reassignOrgToFirm');
      await reassignOrgToFirm({ organizationId: orgId, firmId: firmId || null });

      // Update local state
      setOrganizations(organizations.map(org =>
        org.id === orgId ? { ...org, firmId: firmId || undefined } : org
      ));
    } catch (error: any) {
      console.error('Error reassigning organization:', error);
      alert(error.message || 'Failed to reassign organization');
    }
  };

  // Handle open settings - navigate to org board with settings param
  const handleOpenSettings = async (org: Organization) => {
    await selectOrganization(org.id);
    navigate('/?openSettings=true');
  };

  // Handle update user role
  const handleUpdateRole = async (userId: string, role: UserRole) => {
    try {
      await updateUserRole(userId, role);
      setUsers(users.map(u => u.id === userId ? { ...u, role } : u));
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm(t('admin.deleteUserConfirm'))) return;

    try {
      await deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Handle delete pending user
  const handleDeletePendingUser = async (userId: string) => {
    if (!window.confirm(t('admin.deletePendingConfirm'))) return;

    try {
      await deletePendingUser(userId);
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting pending user:', error);
    }
  };

  // Only org_admin and admin can access this page
  if (!isOrgAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-message">
          <h2>{t('admin.accessDenied')}</h2>
          <p>{t('admin.noPermission')}</p>
          <Link to="/" className="btn-link">{t('admin.goToOrgBoard')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <Link to="/" className="back-link">{t('admin.backToOrgBoard')}</Link>
        <h1>{t('admin.dashboard')}</h1>
        <div className="header-actions">
          <LanguageSelector />
          <div className="user-info">
            <span className="user-name">{user?.name}</span>
            <span className="user-role">{user?.role}</span>
          </div>
          <button onClick={logout} className="btn-logout">{t('nav.logout')}</button>
        </div>
      </header>

      <main className="admin-content">
        {/* Organizations Section - Only for platform admin */}
        {isAdmin && (
          <section className="admin-section">
            <div className="section-header">
              <h2>{t('admin.organizations')}</h2>
              <button
                className="btn-add"
                onClick={() => setShowNewOrgForm(true)}
              >
                {t('admin.newOrganization')}
              </button>
            </div>

            {showNewOrgForm && (
              <form onSubmit={handleCreateOrg} className="new-org-form-card">
                <div className="form-row-inline">
                  <div className="form-field">
                    <label>{t('firm.orgName')}</label>
                    <input
                      type="text"
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                      placeholder={t('firm.orgName')}
                      autoFocus
                      required
                    />
                  </div>
                  <div className="form-field form-field-small">
                    <label>{t('firm.language')}</label>
                    <select
                      value={newOrgLanguage}
                      onChange={(e) => setNewOrgLanguage(e.target.value as 'he' | 'en' | 'ar')}
                    >
                      <option value="he">🇮🇱 עברית</option>
                      <option value="en">🇺🇸 English</option>
                      <option value="ar">🇸🇦 العربية</option>
                    </select>
                  </div>
                  <div className="form-field form-field-small">
                    <label>{language === 'en' ? 'Emoji' : language === 'ar' ? 'رمز' : 'אימוג\'י'}</label>
                    <input
                      type="text"
                      value={newOrgEmoji}
                      onChange={(e) => setNewOrgEmoji(e.target.value)}
                      placeholder="🏢"
                      maxLength={2}
                      className="emoji-input"
                    />
                  </div>
                </div>
                <div className="form-row-inline">
                  <div className="form-field form-field-full">
                    <label>{language === 'en' ? 'Company VFP (Valuable Final Product)' : language === 'ar' ? 'المنتج النهائي القيّم' : 'מוצר סופי בעל ערך'}</label>
                    <textarea
                      value={newOrgVfp}
                      onChange={(e) => setNewOrgVfp(e.target.value)}
                      placeholder={language === 'en' ? 'High-quality products delivered to satisfied customers...' : language === 'ar' ? 'منتجات عالية الجودة تم تسليمها للعملاء الراضين...' : 'מוצרים ושירותים איכותיים שסופקו ללקוחות מרוצים...'}
                      rows={2}
                    />
                  </div>
                </div>
                <div className="form-buttons">
                  <button type="submit" className="btn-primary">{t('firm.create')}</button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowNewOrgForm(false);
                      setNewOrgName('');
                      setNewOrgEmoji('');
                      setNewOrgVfp('');
                    }}
                  >
                    {t('general.cancel')}
                  </button>
                </div>
              </form>
            )}

            {isLoading ? (
              <div className="loading">{t('general.loading')}</div>
            ) : (
              <div className="org-list">
                {organizations.length === 0 ? (
                  <p className="empty-message">{t('admin.noOrganizations')}</p>
                ) : (
                  organizations.map(org => (
                    <div
                      key={org.id}
                      className={`org-card ${selectedOrgId === org.id ? 'selected' : ''}`}
                      onClick={() => handleSelectOrg(org.id)}
                    >
                      <div className="org-info">
                        <div className="org-name-row">
                          {org.emoji && <span className="org-emoji">{org.emoji}</span>}
                          {org.logo && <img src={org.logo} alt="" className="org-logo-small" />}
                          <h3>{org.name}</h3>
                        </div>
                        <div className="org-dates">
                          <span className="org-date">{t('admin.created')}: {formatDate(org.createdAt)}</span>
                          {org.updatedAt && (
                            <span className="org-date">{t('admin.updated')}: {formatDate(org.updatedAt)}</span>
                          )}
                        </div>
                      </div>
                      <div className="org-firm" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={org.firmId || ''}
                          onChange={(e) => handleReassignOrgToFirm(org.id, e.target.value || null)}
                          className="firm-select"
                          title={t('admin.assignToFirm')}
                        >
                          <option value="">{t('admin.noFirmAssigned')}</option>
                          {consultingFirms.map(firm => (
                            <option key={firm.id} value={firm.id}>
                              {firm.branding?.firmName || firm.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="org-actions">
                        <Link
                          to={`/?org=${org.id}`}
                          className="btn-view"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('firm.openBoard')}
                        </Link>
                        <button
                          className="btn-settings"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSettings(org);
                          }}
                        >
                          {t('firm.settings')}
                        </button>
                        <button
                          className="btn-users"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenOrgUsers(org);
                          }}
                        >
                          {t('firm.users')}
                        </button>
                        <button
                          className="btn-clone"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCloneModal(org);
                          }}
                        >
                          {t('firm.clone')}
                        </button>
                        <button
                          className="btn-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOrg(org.id);
                          }}
                        >
                          {t('general.delete')}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {/* Consulting Firms Section - Platform Admin only */}
        {isAdmin && (
          <section className="admin-section">
            <div className="section-header">
              <h2>🏢 {t('admin.consultingFirms')}</h2>
              <button
                className="btn-add"
                onClick={() => setShowNewFirmForm(true)}
              >
                {t('admin.newFirm')}
              </button>
            </div>

            {showNewFirmForm && (
              <form onSubmit={handleCreateFirm} className="inline-form">
                <input
                  type="text"
                  value={newFirmName}
                  onChange={(e) => setNewFirmName(e.target.value)}
                  placeholder={t('admin.firmName')}
                  required
                  autoFocus
                />
                <input
                  type="email"
                  value={newFirmLeadEmail}
                  onChange={(e) => setNewFirmLeadEmail(e.target.value)}
                  placeholder={t('admin.firmLeadEmail')}
                  required
                />
                <button type="submit" className="btn-save" disabled={isCreatingFirm}>
                  {isCreatingFirm ? t('admin.creating') : t('firm.create')}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowNewFirmForm(false)}
                >
                  {t('general.cancel')}
                </button>
              </form>
            )}

            {firmsLoading ? (
              <div className="loading">{t('general.loading')}</div>
            ) : (
              <div className="firm-list">
                {consultingFirms.length === 0 ? (
                  <p className="empty-message">{t('admin.noFirms')}</p>
                ) : (
                  consultingFirms.map(firm => (
                    <div key={firm.id} className="firm-card">
                      <div className="firm-info">
                        {firm.branding?.logo && (
                          <img src={firm.branding.logo} alt="" className="firm-logo-small" />
                        )}
                        <div>
                          <h3>{firm.branding?.firmName || firm.name}</h3>
                          <span className="firm-meta">
                            {t('admin.lead')}: {firm.leadEmail || t('edit.notAssigned')}
                          </span>
                          <div className="firm-stats">
                            <span>👥 {firm.consultantCount || 0} {t('firm.consultants').toLowerCase()}</span>
                            <span>🏢 {firm.clientCount || 0} {t('admin.clients')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="firm-actions">
                        <span className={`firm-status ${firm.isActive ? 'active' : 'inactive'}`}>
                          {firm.isActive ? '✓' : '✗'}
                        </span>
                        <button
                          className="btn-manage"
                          onClick={() => navigate(`/firm?firmId=${firm.id}`)}
                        >
                          {t('admin.manageFirm')}
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteFirm(firm.id)}
                        >
                          {t('general.delete')}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {/* Users Section */}
        <section className="admin-section">
          <div className="section-header">
            <h2>
              {t('admin.userManagement')}
              {isAdmin && selectedOrgId && ` (${organizations.find(o => o.id === selectedOrgId)?.name})`}
              {!isAdmin && organization && ` (${organization.name})`}
            </h2>
            <button
              className="btn-add"
              onClick={() => {
                // For org admins, use their organization; for platform admins, use selected or first org
                const orgId = !isAdmin ? user?.organizationId : (selectedOrgId || organizations[0]?.id);
                setNewUserOrgId(orgId || '');
                setShowNewUserForm(true);
              }}
              disabled={isAdmin && organizations.length === 0}
            >
              {t('admin.newUser')}
            </button>
          </div>

          {showNewUserForm && (
            <AddUserForm
              onSubmit={async (email, name, role, orgId) => {
                const targetOrgId = orgId || newUserOrgId;
                if (!targetOrgId) return;
                const newUser = await createUser(email, name, role, targetOrgId);
                setUsers([...users, newUser]);
                setShowNewUserForm(false);
              }}
              onCancel={() => setShowNewUserForm(false)}
              organizations={organizations}
              showOrgSelector={isAdmin}
              defaultOrgId={newUserOrgId}
              isAdmin={isAdmin}
              availableRoles={['org_user', 'org_admin', 'viewer']}
            />
          )}

          {isLoading ? (
            <div className="loading">{t('general.loading')}</div>
          ) : (
            <UserTable
              users={users}
              currentUserId={user?.id}
              showOrganization={isAdmin}
              organizations={organizations}
              isAdmin={isAdmin}
              onUpdateRole={handleUpdateRole}
              onDeleteUser={(userId, isPending) =>
                isPending ? handleDeletePendingUser(userId) : handleDeleteUser(userId)
              }
              emptyMessage={
                isAdmin && selectedOrgId
                  ? `${t('admin.noUsersFound')}. ${t('admin.selectOrgOrAdd')}`
                  : t('admin.noUsersFound')
              }
            />
          )}
        </section>
      </main>

      {/* Clone Organization Modal */}
      {showCloneModal && cloneSourceOrg && (
        <CloneOrgModal
          org={cloneSourceOrg}
          onClone={handleCloneOrg}
          onClose={() => {
            setShowCloneModal(false);
            setCloneSourceOrg(null);
          }}
        />
      )}

      {/* Org Users Modal */}
      {orgUsersOrg && (
        <OrgManageUsers
          organizationId={orgUsersOrg.id}
          organizationName={orgUsersOrg.name}
          isOpen={showOrgUsersModal}
          onClose={() => {
            setShowOrgUsersModal(false);
            setOrgUsersOrg(null);
          }}
        />
      )}

    </div>
  );
};
